// apps/web/src/features/crm/leads/card/LeadCardClientBar.tsx
//
// Client + contact chips of the card header (#2308, D-3; #2247 FR-4/FR-5).
// Bound: an `EntityChip` for the client (→ `/clients/:id` when the actor can
// read clients), an `EntityChip` for the contact person, a `ContactActions`
// row (real phone/email links when present, plus muted messenger placeholders),
// and a manager unbind behind a `Menu`. Unbound: an accent bind pill that runs
// the reused one-confirmation bind (`LeadClientPicker` → `LeadClientMatchDialog`).
// Presentation only changed here — the mutations are unchanged: unbind PATCHes
// the LEGACY `/leads/:id` (`clientId: null`), never an `/engagements/*` route,
// and every success flushes the Engagement projection so the header re-reads.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ClientLifecycle, type EngagementClientSummary } from '@magimp/shared';
import { engagementsKeys, usePatchLead } from '@magimp/shared-hooks';
import {
  Button,
  ContactActions,
  EntityChip,
  IconButton,
  Menu,
  MenuItem,
  type ContactActionsProps,
} from '../../../../ui/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { errorMessage } from '../../../../api/errors.js';
import { LeadClientMatchDialog } from '../components/LeadClientMatchDialog.js';
import { LeadClientPicker } from '../list/LeadClientPicker.js';

export interface LeadCardClientBarProps {
  leadId: string;
  client: EngagementClientSummary | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  canManage: boolean;
  canReadClients: boolean;
  /** Terminal projection: bind/unbind is steered by backend truth — hide it. */
  terminal: boolean;
}

export function LeadCardClientBar({
  leadId,
  client,
  contactName,
  phone,
  email,
  canManage,
  canReadClients,
  terminal,
}: LeadCardClientBarProps) {
  const { t } = useTranslation(['leads', 'clients', 'common']);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const show = useToastStore((s) => s.show);
  const patch = usePatchLead(leadId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingClientId, setPendingClientId] = useState<string | null>(null);

  const flushProjection = () => void qc.invalidateQueries({ queryKey: engagementsKeys.all });

  const unbind = () =>
    patch.mutate(
      { clientId: null },
      {
        onSuccess: () => {
          show({ tone: 'success', text: t('leads:toasts.clientUnbound') });
          flushProjection();
        },
        onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
      },
    );

  if (client) {
    const actions: ContactActionsProps['actions'] = [];
    if (phone)
      actions.push({
        icon: 'phone',
        href: `tel:${phone}`,
        label: t('leads:card.contactActions.call'),
      });
    if (email)
      actions.push({
        icon: 'mail',
        href: `mailto:${email}`,
        label: t('leads:card.contactActions.email'),
      });
    actions.push({
      icon: 'send-horizontal',
      href: null,
      label: t('leads:card.contactActions.telegram'),
    });
    actions.push({
      icon: 'message-circle',
      href: null,
      label: t('leads:card.contactActions.whatsapp'),
    });
    actions.push({ icon: 'message-square', href: null, label: t('leads:card.contactActions.max') });

    return (
      <div
        data-testid="lead-card-client-bar"
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}
      >
        <EntityChip
          icon="building-2"
          label={client.name}
          meta={<LifecycleMeta lifecycle={client.lifecycle} />}
          title={t('leads:client.openClient')}
          // Omit onClick entirely (not `undefined`) without clients.read so the
          // chip renders as a static, non-navigating pill.
          {...(canReadClients ? { onClick: () => navigate(`/clients/${client.id}`) } : {})}
        />
        <EntityChip avatarName={contactName ?? ''} label={contactName ?? t('leads:card.unnamed')} />
        <ContactActions actions={actions} />
        {canManage && !terminal && (
          <Menu
            trigger={(triggerProps) => (
              <IconButton
                {...triggerProps}
                icon="more-vertical"
                label={t('leads:card.clientActions')}
                size={26}
              />
            )}
          >
            <MenuItem onClick={unbind}>
              <span data-testid="lead-card-unbind">{t('leads:client.unbind')}</span>
            </MenuItem>
          </Menu>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="lead-card-client-bar"
      style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, color: 'var(--fg-2)' }}>{t('leads:client.none')}</span>
        {canManage && !terminal && (
          <Button
            kind="secondary"
            size="sm"
            icon="user-plus"
            onClick={() => setPickerOpen((v) => !v)}
            data-testid="lead-card-bind-toggle"
            style={{
              borderColor: 'var(--accent-1)',
              background: 'var(--accent-1-subtle)',
              color: 'var(--accent-1-press)',
            }}
          >
            {pickerOpen ? t('common:actions.cancel') : t('leads:client.bind')}
          </Button>
        )}
      </div>
      {pickerOpen && !pendingClientId && (
        <LeadClientPicker onPick={(c) => setPendingClientId(c.id)} />
      )}
      {pendingClientId && (
        <LeadClientMatchDialog
          leadId={leadId}
          clientId={pendingClientId}
          onClose={() => setPendingClientId(null)}
          onBound={() => {
            setPendingClientId(null);
            setPickerOpen(false);
            flushProjection();
          }}
        />
      )}
    </div>
  );
}

function LifecycleMeta({ lifecycle }: { lifecycle: ClientLifecycle }) {
  const { t } = useTranslation(['clients']);
  const working = lifecycle === ClientLifecycle.WORKING;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: working ? 'var(--status-success-fg)' : 'var(--status-warn-fg)',
        }}
      />
      <span data-testid="lead-card-lifecycle">
        {working ? t('clients:lifecycle.working') : t('clients:lifecycle.potential')}
      </span>
    </span>
  );
}
